import { useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";
import { IS_DEMO } from "../env";

type Health = {
  id: number | string;
  provider: string;
  status: string;
  consecutive_failures?: number | null;
};
export const healthLevel = (h: Health): "ok" | "warn" | "error" =>
  ["error", "disconnected", "late", "failing"].includes(h.status)
    ? "error"
    : h.status === "needs_reauth" || (h.consecutive_failures ?? 0) > 0
      ? "warn"
      : "ok";

/**
 * Red/yellow dots per failing integration, linking to Santé du système (spec 09 §4). Text label with
 * every dot. Hidden in the demo and whenever the view is unavailable (no error toast).
 */
export const IntegrationHealthStrip = () => {
  const translate = useTranslate();
  const { data, error } = useGetList<Health>(
    "v_integration_health",
    { pagination: { page: 1, perPage: 50 } },
    { enabled: !IS_DEMO, retry: false },
  );
  if (IS_DEMO || error || !data) return null;
  const issues = data.filter((h) => healthLevel(h) !== "ok");
  return (
    <Link
      to="/parametres/sante"
      className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border bg-card px-4 py-2 text-sm"
    >
      <span className="font-bold">
        {translate("gavion.dashboard.health.title")}
      </span>
      {issues.length === 0 ? (
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <span
            className="size-2.5 rounded-full bg-[#10B981] dark:bg-[#34D399]"
            aria-hidden="true"
          />
          {translate("gavion.dashboard.health.all_ok")}
        </span>
      ) : (
        issues.map((h) => (
          <span key={h.id} className="flex items-center gap-1.5">
            <span
              className={`size-2.5 rounded-full ${healthLevel(h) === "error" ? "bg-destructive" : "bg-primary"}`}
              aria-hidden="true"
            />
            {translate(`gavion.dashboard.health.providers.${h.provider}`, {
              _: h.provider,
            })}{" "}
            — {translate(`gavion.dashboard.health.levels.${healthLevel(h)}`)}
          </span>
        ))
      )}
    </Link>
  );
};
